import Button from 'components/Form/Button';
import SearchInput from 'components/Form/SearchInput';
import { Icon, Icons } from 'components/Icon';
import React, { useCallback, useEffect, useState } from 'react';
import male from '../../../../../../../assets/male.png';
import ConfirmModal from 'components/Modal/ConfirmModal';
import AddBeneficiaryModal from '../../../Beneficiary/AddBeneficiaryModal';
import { Link, useParams } from 'react-router-dom';
import CircleProgressBar from 'components/CircleProgressBar';
import BondAssignAssetModal from './BondAssignAssetModal';
import {
  useGetBondDetails,
  useUnAssignBondBeneficiary
} from 'common/queries-and-mutations/bond';
import { useGetBeneficiaryList } from 'common/queries-and-mutations/beneficiary';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import { BeneficiaryDto } from 'service/dto/beneficiary.dto';
import { Loader } from 'components/Loader';
import { StringFormater } from 'utils';
import AlertNotification from 'components/AlertNotification';
import withCreatePortal from 'components/HOC/withCreatePortal';

const EnhancedConfirmModal = withCreatePortal(ConfirmModal);
const EnhancedAddBeneficiaryForm = withCreatePortal(AddBeneficiaryModal);
const EnhancedBondAssignAsset = withCreatePortal(BondAssignAssetModal);

export default function BondAssetBeneficiaryList() {
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isExecutingSearch, setIsExecutingSearch] = useState<boolean>(true);
  const [beneficiary, setBeneficiary] = useState<{
    beneficiaryName: string;
    beneficiaryId: number | null;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { bondId } = useParams();

  const {
    data: asset,
    isLoading,
    isFetched,
    refetch: getAssetDetails
  } = useGetBondDetails({
    bondId: +bondId!
  });

  const {
    data: beneficiaries,
    isLoading: beneficiaryLoading,
    refetch
  } = useGetBeneficiaryList({
    enabled: isExecutingSearch,
    search: searchQuery
  });
  const {
    mutate: removeBeneficiary,
    isSuccess,
    isError
  } = useUnAssignBondBeneficiary();

  const executeSearch = debounce(() => {
    setIsExecutingSearch(true);
  }, 2000);

  const handleChange = (val: any) => {
    setIsExecutingSearch(false);
    if (val === '') {
      setSearchQuery('');
      executeSearch();
    }
    if (val.trim()) {
      setSearchQuery(val);
      executeSearch();
    }
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      setShowAskToConfirm(false);
      setBeneficiary(null);
      getAssetDetails();
      refetch();
      toast(<Alert message="Asset Successfully Removed" type="success" />);
    } else if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, refetch, getAssetDetails]);

  const handleRemoveBeneficiary = () => {
    const allocation = asset?.allocations.find(
      allocation =>
        allocation.beneficiaryId === beneficiary?.beneficiaryId &&
        allocation.bondId === Number(bondId)
    );
    if (allocation) {
      setIsSubmitting(true);
      removeBeneficiary({
        allocationId: allocation.id!
      });
    }
  };

  const isAssignToThisAsset = useCallback(
    (beneficiary: BeneficiaryDto, beneficiaryId: number | null) => {
      return beneficiary.bondAllocations.find(
        asset =>
          asset.bondId === Number(bondId) &&
          asset.beneficiaryId === beneficiaryId
      );
    },
    [bondId]
  );

  if (isLoading && isFetched && beneficiaryLoading) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      {asset && !beneficiaryLoading && (
        <>
          <div className="my-4 flex items-center justify-between font-circular font-bold text-[#FF6600]">
            <div className="flex items-center gap-4">
              <span className="text-xl">
                Add a beneficiary - {asset?.issuerName} {asset?.ISINNo}
              </span>
              <div className="mr-9 flex items-center gap-2">
                <CircleProgressBar
                  progress={asset.remainingAlloted}
                  width={30}
                  height={50}
                />
                <div className="flex items-center gap-1 text-base text-[#868686]">
                  <span>{asset.remainingAlloted}%</span>
                  <span>Remaining</span>
                </div>
              </div>
            </div>
            <Link
              to={`/dashboard/assets/bond/details/${bondId}`}
              className="flex items-center gap-4"
            >
              <Icon name={Icons.ArrowLeft} />
              <span className="text-base">Back to Allocation</span>
            </Link>
          </div>
          <div className="mt-4 flex flex-col">
            <div className="h-fit w-full rounded-md bg-white pb-28 shadow-sm">
              <div className="border-b border-[#E5E5E5]">
                <div className="flex items-center justify-between p-3 px-5">
                  <span className="text-xl font-medium text-[#353535]">
                    Beneficiary list
                  </span>
                  <div className="flex items-center gap-3">
                    <SearchInput
                      name="search"
                      placeholder="Search beneficiary"
                      value={searchQuery}
                      onChange={e => handleChange(e.target.value)}
                      size="lg"
                      className="h-11 w-[20.1rem] rounded border border-[#C3C3C3] bg-white"
                    />
                    <Button
                      variant="outline"
                      size="custom"
                      className="w-[20.1rem] place-self-end rounded border-[#FF7A00] bg-[#FF7A00] p-3 text-center text-xs font-medium text-white"
                      label="Create new beneficiary"
                      iconPosition="left"
                      icon={
                        <Icon
                          name={Icons.Add}
                          fill="#FFFFFF"
                          width={12}
                          height={12}
                        />
                      }
                      onClick={() => {
                        setShowAddModal(true);
                      }}
                    ></Button>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                {beneficiaries && beneficiaries?.length > 0 ? (
                  <table className="w-full text-left text-sm font-normal text-[#353535]">
                    <thead className="text-xs text-[#1F4173]">
                      <tr>
                        <th scope="col" className="px-6 py-3">
                          Name
                        </th>
                        <th scope="col" className="px-6 py-3">
                          E-mail address
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Relationship
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Guardian
                        </th>
                        <th scope="col" className="px-6 py-3">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(beneficiaries || []).map(beneficiary => (
                        <tr className="border-b bg-white" key={beneficiary.id}>
                          <td
                            scope="row"
                            className="flex items-center whitespace-nowrap px-6 py-4"
                          >
                            <img
                              className="h-10 w-10 rounded-full"
                              src={male}
                            />
                            <div className="ps-3 font-normal">
                              <span>{`${beneficiary?.beneficiaryFirstName} ${
                                beneficiary?.beneficiaryMiddleName || ''
                              } ${beneficiary?.beneficiaryLastName}`}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {beneficiary.emailAddress}
                          </td>
                          <td className="px-6 py-4">
                            {StringFormater(beneficiary.relationship)}
                          </td>
                          <td className="px-6 py-4">
                            {beneficiary?.children?.guardian
                              ? StringFormater(
                                  `${beneficiary?.children?.guardian
                                    ?.guardianFirstName} ${
                                    beneficiary?.children?.guardian
                                      ?.guardianMiddleName || ''
                                  } ${beneficiary?.children?.guardian
                                    ?.guardianLastName}`
                                )
                              : 'N/A'}
                          </td>
                          {isAssignToThisAsset(beneficiary, beneficiary.id) ? (
                            <td className="p-4 pl-5">
                              <button
                                type="button"
                                className="font-medium text-[#E30000]"
                                onClick={() => {
                                  setShowAskToConfirm(true);
                                  setBeneficiary({
                                    beneficiaryId: beneficiary.id,
                                    beneficiaryName: `${beneficiary?.beneficiaryFirstName} ${
                                      beneficiary?.beneficiaryMiddleName || ''
                                    } ${beneficiary?.beneficiaryLastName}`
                                  });
                                }}
                              >
                                Remove
                              </button>
                            </td>
                          ) : (
                            <td className="py-4 text-center">
                              <Button
                                variant="outline"
                                size="custom"
                                className="place-self-end rounded border-[#FF7A00] bg-[#FFF8F2] px-8 py-1.5 text-center text-xs font-medium text-[#FF7A00]"
                                label="Assign"
                                onClick={() => {
                                  setBeneficiary({
                                    beneficiaryId: beneficiary.id,
                                    beneficiaryName: `${beneficiary?.beneficiaryFirstName} ${
                                      beneficiary?.beneficiaryMiddleName || ''
                                    } ${beneficiary?.beneficiaryLastName}`
                                  });
                                  setShowAssignModal(true);
                                }}
                              ></Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <AlertNotification
                    type="success"
                    message="All your beneficiary will be displayed here. Additionally, you can utilize the button above to add a new beneficiary."
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {showAskToConfirm && (
        <EnhancedConfirmModal
          content={`Are you sure you want to remove the user? This can't be undone. Confirm if you want to continue.`}
          onConfirm={() => {
            handleRemoveBeneficiary();
          }}
          onCancel={() => {
            setShowAskToConfirm(false);
          }}
          actionText="Yes"
          cancelText="No"
          title="Action Required"
          isSubmitting={isSubmitting}
        />
      )}
      {showAddModal && (
        <EnhancedAddBeneficiaryForm
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            refetch();
          }}
        />
      )}
      {showAssignModal && (
        <EnhancedBondAssignAsset
          onClose={() => {
            setShowAssignModal(false);
          }}
          onSuccess={() => {
            setShowAssignModal(false);
            getAssetDetails();
            refetch();
          }}
          assetName={`${asset?.issuerName} - ${asset?.ISINNo}`}
          bondId={Number(bondId)}
          beneficiaryId={beneficiary?.beneficiaryId}
          remainingAlloted={(asset ?? {}).remainingAlloted ?? 0}
          beneficiaryName={beneficiary?.beneficiaryName}
        />
      )}
    </div>
  );
}
